import { useEffect, useMemo, useState } from 'react';
import { BiChevronRight, BiErrorCircle, BiRightArrowAlt } from 'react-icons/bi';
import { BsPencil, BsThreeDots, BsTrash } from 'react-icons/bs';
import { FaRegCalendar } from 'react-icons/fa6';
import { HiOutlineUsers } from 'react-icons/hi2';
import { IoMdClose } from 'react-icons/io';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

import type { CreateTaskPayload } from '@/components/CreateSheet/CreateEditTaskSheet';
import CreateEditTaskSheet from '@/components/CreateSheet/CreateEditTaskSheet';
import { GuestInvitationModal } from '@/components/GuestInvitationModal/GuestInvitationModal';
import { useInviteRights } from '@/hooks/useInviteRights';
import { usePersonsMap } from '@/hooks/usePersonsMap';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import type { InviteEmailTemplate } from '@/store/auth/auth.dto';
import { useAuth } from '@/store/auth/auth.store';
import { getEventDetail } from '@/store/events/events.api';
import {
  type EventResponse,
  headcount,
  partyHeadcount,
  type SubActivity,
  type SubEventInput,
  sumHeadcount,
} from '@/store/events/events.dto';
import { useEvents } from '@/store/events/events.store';
import {
  pendingGuestInvites,
  type GuestInvitee,
  useEventInviteAnswersQuery,
  useEventInvitesQuery,
  useGuestInviteActions,
  useInviteGuestsMutation,
} from '@/store/invites/invites.store';
import { usePerson } from '@/store/persons/person.store';
import { type TaskResponse, TaskStatus } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { formatEventDateRange } from '@/utils/formatEventDateRange';
import { formatIsoToTime12h } from '@/utils/timeFormatter';
import { toast } from '@/utils/toast';

import { Accordion } from '../Accordion/Accordion';
import { ConfirmationModal } from '../ConfirmationModal';
import ContextMenu from '../ContextMenu/ContextMenu';
import CreateItinerarySheet from '../CreateSheet/EditEventSheet/ItinerarySection/CreateItinerarySheet/CreateItinerarySheet';
import { SidePanel } from '../SidePanel';

import { EventChatButtons } from './EventChatButtons';
import s from './EventSidePanel.module.scss';
import { GuestsSection } from './GuestsSection';
import { TasksSection } from './TasksSection';

interface SubActivitySidePanelProps {
  subActivity: SubActivity | null;
  parentEvent: EventResponse | null;
  onClose: () => void;
  /** Fires when a chat is opened from this panel. */
  onChatOpen?: () => void;
  onOpenParent?: () => void;
  onEditSubActivity?: (id: string, data: SubEventInput) => void;
  onTaskStatusChange?: (taskId: string, serviceId: string, status: TaskStatus) => void;
}

function SubActivitySidePanel({
  subActivity,
  parentEvent,
  onClose,
  onChatOpen,
  onOpenParent,
  onEditSubActivity,
  onTaskStatusChange,
}: SubActivitySidePanelProps) {
  const [subMenuAnchor, setSubMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [editingSubActivity, setEditingSubActivity] = useState(false);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [editingTask, setEditingTask] = useState<TaskResponse | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isTaskSheetOpen, setIsTaskSheetOpen] = useState(false);
  const [isGuestInvitationOpen, setIsGuestInvitationOpen] = useState(false);
  const [isGuestsOpen, setIsGuestsOpen] = useState(false);
  const updateSubActivity = useEvents((state) => state.updateSubActivity);
  const deleteSubActivity = useEvents((state) => state.deleteSubActivity);
  const { createTask, updateTask, removeTask, getTasksByEventId, updateTaskStatus } = useTasks();
  const tasksList = useTasks((s) => s.tasksList);
  const navigate = useNavigate();

  const personsMap = usePersonsMap();
  const getPeople = usePerson((state) => state.getPeople);
  // Fresh detail for this sub-activity, refetched on open and after edits (the prop is
  // frozen on calendar hosts). Keyed by id and never blanked, so reopening doesn't flash
  // the stale prop data.
  const [fresh, setFresh] = useState<EventResponse | null>(null);
  const [detailVersion, setDetailVersion] = useState(0);
  const { can } = usePermissions();
  const canActOnEvents = can(PERMISSIONS.activity.EventsActions);
  const isOwnerOrCaptain = canActOnEvents;
  const isGuest = !canActOnEvents;
  const { canInvite } = useInviteRights();
  const currentUserId = useAuth((state) => state.session?.id);
  const { mutateAsync: inviteGuests } = useInviteGuestsMutation();
  const { vessel } = useVessels();

  const handleTaskStatusChange = (taskId: string, serviceId: string, status: TaskStatus) => {
    setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, status } : task)));

    if (onTaskStatusChange) {
      void onTaskStatusChange(taskId, serviceId, status);
      return;
    }

    void updateTaskStatus({ id: taskId, serviceId, status });
  };

  useEffect(() => {
    if (!subActivity?.id) return;
    let cancelled = false;
    getTasksByEventId(subActivity.id).then((result) => {
      if (!cancelled) setTasks(result);
    });
    return () => {
      cancelled = true;
    };
  }, [subActivity?.id, getTasksByEventId, tasksList]);

  // Refetch this sub-activity's detail + people on open so a guest who accepted is reflected
  useEffect(() => {
    if (!subActivity?.id) return;
    let cancelled = false;
    void getPeople();
    getEventDetail(subActivity.id)
      .then((detail) => {
        if (!cancelled) setFresh(detail);
      })
      .catch(() => {
        /* fall back to prop data */
      });
    return () => {
      cancelled = true;
    };
  }, [subActivity?.id, getPeople, detailVersion]);

  const displaySub = useMemo(
    () =>
      subActivity && fresh && fresh.id === subActivity.id
        ? ({ ...subActivity, ...fresh } as SubActivity)
        : subActivity,
    [subActivity, fresh],
  );

  const participants = useMemo(() => displaySub?.participants ?? [], [displaySub]);

  const guests = useMemo(() => {
    return participants
      .filter((p) => p.type === 'guest')
      .map((p) => personsMap.get(p.person_id))
      .filter((v): v is NonNullable<typeof v> => !!v);
  }, [participants, personsMap]);

  // Guests who declined before accepting never get a people_roles entry, so
  // personsMap has no record for them. Synthesise Guest objects from the
  // participant name stored when AddDeclinedParticipant was called.
  const inviteOnlyDeclinedGuests = useMemo(
    () =>
      participants
        .filter(
          (p) => p.type === 'guest' && p.status === 'declined' && !personsMap.has(p.person_id),
        )
        .map((p) => {
          const [firstName = '', ...rest] = (p.name ?? '').split(' ');
          return {
            id: p.person_id,
            person_info: { first_name: firstName, last_name: rest.join(' ') },
          };
        }),
    [participants, personsMap],
  );
  const allGuests = useMemo(
    () => [...guests, ...inviteOnlyDeclinedGuests],
    [guests, inviteOnlyDeclinedGuests],
  );

  // Pending guest invites for this sub-activity (persistent).
  const { data: guestInvites } = useEventInvitesQuery(subActivity?.id, vessel?.id);
  const { data: answers = [] } = useEventInviteAnswersQuery(subActivity?.id, vessel?.id);
  const { changeDeadline } = useGuestInviteActions();
  const pendingInvites = pendingGuestInvites(
    guestInvites?.items,
    new Set(allGuests.map((g) => g.id)),
    answers,
  );

  const handleEditTask = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      setEditingTask(task);
      setIsTaskSheetOpen(true);
    }
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    await removeTask(taskToDelete);
    setTasks((prev) => prev.filter((t) => t.id !== taskToDelete));
    setTaskToDelete(null);
  };

  const handleInviteGuest = async (
    invitees: GuestInvitee[],
    expiresAt: string | undefined,
    emailTemplate: InviteEmailTemplate,
    message: string | undefined,
  ) => {
    if (!subActivity || !vessel) return;

    await inviteGuests({
      invitees,
      vessel,
      event: { id: subActivity.id, expires_at: expiresAt },
      emailTemplate,
      message,
    });
    setIsGuestInvitationOpen(false);
  };

  // Every count is in people: each guest counts with the guests they bring.
  const guestParticipants = participants.filter((p) => p.type === 'guest');
  const confirmedCount =
    sumHeadcount(
      guestParticipants.filter((p) => p.status === 'accepted'),
      headcount,
    ) +
    sumHeadcount(
      answers.filter((a) => a.accepted),
      partyHeadcount,
    );
  const declinedCount =
    sumHeadcount(
      guestParticipants.filter((p) => p.status === 'declined'),
      headcount,
    ) +
    sumHeadcount(
      answers.filter((a) => !a.accepted),
      partyHeadcount,
    );
  const awaitingCount =
    sumHeadcount(
      guestParticipants.filter((p) => p.status !== 'accepted' && p.status !== 'declined'),
      headcount,
    ) + sumHeadcount(pendingInvites, partyHeadcount);
  const invitedCount = confirmedCount + declinedCount + awaitingCount;

  const confirmDelete = async () => {
    if (!subActivity) return;

    try {
      await deleteSubActivity(subActivity.id, parentEvent?.id);
      toast.success('Activity deleted');
      setDeleteOpen(false);
      onClose();
    } catch {
      toast.error('Couldn’t delete the activity. Please try again.');
      setDeleteOpen(false);
    }
  };

  return (
    <SidePanel open={!!subActivity} onClose={onClose} zIndex={1300}>
      {displaySub && (
        <div className={s.root}>
          <header className={s.hero}>
            <div className={s.heroBar}>
              <button
                type="button"
                className={s.heroButton}
                onClick={onClose}
                aria-label="Close Sub-Activity Details"
              >
                <IoMdClose />
              </button>
              {!isGuest && (
                <button
                  type="button"
                  className={s.heroButton}
                  onClick={(e) => setSubMenuAnchor(e.currentTarget)}
                  aria-label="Sub-Activity Actions"
                >
                  <BsThreeDots />
                </button>
              )}
            </div>

            <h2 className={s.heroTitle}>{displaySub.title}</h2>

            <div className={s.heroMeta}>
              <span className={s.heroType}>
                <FaRegCalendar aria-hidden="true" /> Itinerary
              </span>
              {parentEvent && (
                <button
                  type="button"
                  className={s.parentLink}
                  onClick={() => (onOpenParent ? onOpenParent() : onClose())}
                  aria-label={`Open Parent Event ${parentEvent.title}`}
                >
                  <BiRightArrowAlt size={18} />
                  <span>{parentEvent.title}</span>
                </button>
              )}
            </div>

            <p className={s.heroWhen}>
              {formatEventDateRange(displaySub.time_slot.start, displaySub.time_slot.end)} ·{' '}
              {formatIsoToTime12h(displaySub.time_slot.start)} –{' '}
              {formatIsoToTime12h(displaySub.time_slot.end)}
            </p>
          </header>

          {displaySub.description && (
            <section className={s.section}>
              <div className={`md-content ${s.description}`}>
                <ReactMarkdown>{displaySub.description}</ReactMarkdown>
              </div>
            </section>
          )}

          <section className={s.section} aria-label="Guests">
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>
                <HiOutlineUsers aria-hidden="true" /> Guests
              </span>
              <button
                type="button"
                className={s.sectionLink}
                onClick={() => setIsGuestsOpen((prev) => !prev)}
              >
                {isGuestsOpen ? 'Hide List' : 'View List'} <BiChevronRight aria-hidden="true" />
              </button>
            </div>
            <div className={s.stats}>
              <div className={s.stat}>
                <span className={s.statValue}>{invitedCount}</span>
                <span className={s.statLabel}>All</span>
              </div>
              <div className={s.stat}>
                <span className={s.statValue}>{confirmedCount}</span>
                <span className={s.statLabel}>
                  <span className={`${s.statDot} ${s.statConfirmed}`} aria-hidden="true" />
                  Confirmed
                </span>
              </div>
              <div className={s.stat}>
                <span className={s.statValue}>{awaitingCount}</span>
                <span className={s.statLabel}>
                  <span className={`${s.statDot} ${s.statAwaiting}`} aria-hidden="true" />
                  Invited
                </span>
              </div>
              <div className={s.stat}>
                <span className={s.statValue}>{declinedCount}</span>
                <span className={s.statLabel}>
                  <span className={`${s.statDot} ${s.statDeclined}`} aria-hidden="true" />
                  Declined
                </span>
              </div>
            </div>
            {isGuestsOpen && (
              <GuestsSection
                guests={allGuests}
                participants={participants}
                isOwnerOrCaptain={isOwnerOrCaptain}
                currentUserId={currentUserId}
                showInvite={canInvite}
                onInviteClick={() => setIsGuestInvitationOpen(true)}
                pendingInvites={pendingInvites}
                answers={answers}
                showDeadlines={!isGuest}
                onChangeDeadline={changeDeadline}
                deadlineMax={displaySub.time_slot?.end ? new Date(displaySub.time_slot.end) : null}
              />
            )}
          </section>

          <section className={s.section} aria-label="Tasks">
            <Accordion title={`Tasks (${tasks.length})`} openedByDefault>
              <TasksSection
                tasks={tasks}
                onTaskStatusChange={handleTaskStatusChange}
                onEdit={handleEditTask}
                onDelete={setTaskToDelete}
                showAddTask={!isGuest}
                onAddTaskClick={() => {
                  setEditingTask(null);
                  setIsTaskSheetOpen(true);
                }}
              />
            </Accordion>
          </section>

          <div className={s.actions}>
            <EventChatButtons
              vesselId={vessel?.id}
              isGuest={isGuest}
              eventChatId={parentEvent?.temp_chat_id}
              closePanel={() => (onChatOpen ? onChatOpen() : onClose())}
            />
          </div>

          <footer className={s.footer}>
            <button
              type="button"
              className={s.footerLink}
              onClick={() => navigate('/safety-checklist')}
            >
              <BiErrorCircle aria-hidden="true" /> Emergency plan
            </button>
          </footer>
        </div>
      )}

      <ContextMenu
        isOpen={!!subMenuAnchor}
        onClose={() => setSubMenuAnchor(null)}
        anchorEl={subMenuAnchor}
        items={[
          {
            label: 'Edit',
            icon: <BsPencil size={14} />,
            onClick: () => setEditingSubActivity(true),
          },
          {
            label: 'Delete',
            icon: <BsTrash size={14} />,
            variant: 'danger',
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />

      <ConfirmationModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void confirmDelete()}
        title="Delete Activity"
        message={`Would you like to delete “${displaySub?.title ?? ''}”? This can’t be undone.`}
      />

      {displaySub && (
        <CreateItinerarySheet
          key={`${displaySub.id}-${detailVersion}`}
          open={editingSubActivity}
          onClose={() => setEditingSubActivity(false)}
          initialData={{
            title: displaySub.title,
            description: displaySub.description,
            time_slot: {
              start: displaySub.time_slot.start,
              end: displaySub.time_slot.end,
            },
            participants: displaySub.participants,
          }}
          onSubmit={async (data) => {
            const payload: SubEventInput = {
              title: data.title,
              description: data.description,
              time_slot: {
                start: data.time_slot.start,
                end: data.time_slot.end,
              },
              participants: data.participants,
            };
            await updateSubActivity(displaySub.id, payload, parentEvent);
            onEditSubActivity?.(displaySub.id, payload);
            setDetailVersion((version) => version + 1);
            setEditingSubActivity(false);
          }}
        />
      )}
      {isGuestInvitationOpen && subActivity && (
        <GuestInvitationModal
          onClose={() => setIsGuestInvitationOpen(false)}
          onInvite={handleInviteGuest}
          eventName={displaySub?.title ?? ''}
          eventEndsAt={displaySub?.time_slot?.end}
        />
      )}
      <ConfirmationModal
        open={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={confirmDeleteTask}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
      />
      <CreateEditTaskSheet
        key={editingTask?.id ?? 'create'}
        open={isTaskSheetOpen}
        onClose={() => setIsTaskSheetOpen(false)}
        title={editingTask ? 'Edit Task' : 'Add Task'}
        hideWorkflowStatuses
        eventId={subActivity?.id}
        initialData={editingTask ?? undefined}
        onSubmitTask={async (payload: CreateTaskPayload) => {
          if (editingTask) {
            await updateTask({ ...editingTask, ...payload } as unknown as TaskResponse);
          } else {
            await createTask(payload);
          }
          if (subActivity?.id) {
            const updated = await getTasksByEventId(subActivity.id);
            setTasks(updated);
          }
          setIsTaskSheetOpen(false);
        }}
      />
    </SidePanel>
  );
}

export default SubActivitySidePanel;
